import { useState } from 'react';
import { Pencil, Trash2, X } from '../../lib/icons';
import { toast } from 'sonner';
import { useAuth } from '../../lib/auth';
import { estAdmin } from '../../lib/permissions';

const inputCls =
  'mt-1.5 h-11 w-full border border-slate-300 bg-slate-50 px-3 font-data text-sm text-slate-900 outline-none transition-colors duration-150 focus:border-slate-900 focus:bg-white';
const label = 'text-xs font-bold uppercase tracking-[0.1em] text-slate-500';

// Colonne « Actions » réservée à l'Administrateur : correction (override) et suppression en cascade.
export const ActionsAdmin = ({ id, champs, row, onModifier, onSupprimer, onDone, titre = 'Corriger l’opération' }) => {
  const { user } = useAuth();
  const [edition, setEdition] = useState(null);
  const [confirme, setConfirme] = useState(false);
  const [refus, setRefus] = useState(null);
  if (!estAdmin(user?.role)) return null;

  const ouvrir = () =>
    setEdition(Object.fromEntries(champs.map((c) => [c.key, c.type === 'bool' ? (row[c.key] ? 'oui' : 'non') : row[c.key] ?? ''])));

  const enregistrer = async (e) => {
    e.preventDefault();
    const patch = Object.fromEntries(
      champs.map((c) => [
        c.key,
        c.type === 'number' ? Number(edition[c.key]) || 0 : c.type === 'bool' ? edition[c.key] === 'oui' : edition[c.key],
      ])
    );
    const { error } = await onModifier(patch);
    if (error) return toast.error(error);
    toast.success(`${id} corrigé — soldes et totaux recalculés`);
    setEdition(null);
    onDone?.();
  };

  // Refus serveur (ligne protégée, période fermée, base indisponible…) : la fenêtre
  // RESTE ouverte avec le message exact. Jamais de succès ni de disparition de ligne.
  const supprimer = async () => {
    const { error } = await onSupprimer();
    if (error) {
      setRefus(error);
      return toast.error(error);
    }
    toast.success(`${id} supprimé — impacts annulés (tracé au journal d’audit)`);
    setRefus(null);
    setConfirme(false);
    onDone?.();
  };

  return (
    <div className="flex justify-end gap-1.5">
      <button
        type="button"
        data-testid={`admin-modifier-${id}`}
        title="Corriger (Administrateur)"
        onClick={ouvrir}
        className="border border-slate-300 p-1.5 text-slate-600 transition-colors duration-150 hover:bg-slate-900 hover:text-white"
      >
        <Pencil size={13} />
      </button>
      <button
        type="button"
        data-testid={`admin-supprimer-${id}`}
        title="Annuler la pièce (Administrateur) — conservée en historique"
        onClick={() => {
          setRefus(null);
          setConfirme(true);
        }}
        className="border border-red-300 p-1.5 text-red-600 transition-colors duration-150 hover:bg-red-600 hover:text-white"
      >
        <Trash2 size={13} />
      </button>

      {edition && (
        <div data-testid="admin-override-overlay" className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/60 p-4">
          <form onSubmit={enregistrer} data-testid="admin-override-form" className="max-h-[92vh] w-full max-w-lg overflow-y-auto border border-slate-200 bg-white text-left">
            <div className="flex items-start justify-between border-b border-slate-200 px-5 py-4">
              <div>
                <h2 className="font-heading text-lg font-black tracking-tight text-slate-900">{titre}</h2>
                <p className="font-data text-[11px] uppercase tracking-[0.1em] text-slate-500">
                  {id} — override administrateur
                </p>
              </div>
              <button type="button" data-testid="admin-override-close" onClick={() => setEdition(null)} className="border border-slate-200 p-2 text-slate-500 hover:bg-slate-900 hover:text-white">
                <X size={16} />
              </button>
            </div>
            <div className="grid grid-cols-1 gap-4 px-5 py-5 sm:grid-cols-2">
              {champs.map((c) => (
                <div key={c.key} className={c.large ? 'sm:col-span-2' : ''}>
                  <label className={label}>{c.label}</label>
                  {c.options ? (                    <select
                      data-testid={`override-${c.key}`}
                      value={edition[c.key]}
                      onChange={(e) => setEdition((p) => ({ ...p, [c.key]: e.target.value }))}
                      className={inputCls}
                    >
                      {c.options.map((o) => (
                        <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      data-testid={`override-${c.key}`}
                      type={c.type === 'number' ? 'number' : c.type === 'date' ? 'date' : 'text'}
                      step={c.type === 'number' ? '0.001' : undefined}
                      value={edition[c.key]}
                      onChange={(e) => setEdition((p) => ({ ...p, [c.key]: e.target.value }))}
                      className={inputCls}
                    />
                  )}
                </div>
              ))}
            </div>
            <div className="flex gap-3 border-t border-slate-200 px-5 py-4">
              <button type="submit" data-testid="admin-override-enregistrer" className="h-11 flex-1 bg-slate-900 font-heading text-sm font-bold uppercase tracking-[0.08em] text-white transition-colors duration-150 hover:bg-slate-700">
                Forcer la mise à jour
              </button>
              <button type="button" onClick={() => setEdition(null)} className="h-11 border border-slate-300 px-5 text-sm font-semibold text-slate-700 hover:bg-slate-100">
                Annuler
              </button>
            </div>
          </form>
        </div>
      )}

      {confirme && (
        <div data-testid="admin-suppression-overlay" className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/60 p-4">
          <div className="w-full max-w-md border border-slate-200 bg-white p-5 text-left">
            <h2 className="font-heading text-lg font-black tracking-tight text-slate-900">Annuler {id} ?</h2>
            <p className="mt-2 text-sm text-slate-600">
              La ligne passe au statut « Annulé » et tous ses impacts sont annulés (cuves, soldes, caisse). Elle
              n'est pas effacée : elle reste en base (piste d'audit) et dans le journal, visibles par l'Administrateur.
            </p>
            <div className="mt-5 flex gap-3">
              <button type="button" data-testid="admin-suppression-confirmer" onClick={supprimer} className="h-11 flex-1 bg-red-600 font-heading text-sm font-bold uppercase tracking-[0.08em] text-white transition-colors duration-150 hover:bg-red-700">
                Annuler la pièce
              </button>
              <button type="button" data-testid="admin-suppression-annuler" onClick={() => { setRefus(null); setConfirme(false); }} className="h-11 border border-slate-300 px-5 text-sm font-semibold text-slate-700 hover:bg-slate-100">
                Annuler
              </button>
            </div>
            {refus && (
              <p data-testid="admin-suppression-erreur" className="mt-4 border-l-4 border-red-600 bg-red-50 px-3 py-2 text-sm font-semibold text-red-800">
                Refusé par la base : {refus}
                <span className="mt-1 block font-normal">
                  La ligne est CONSERVÉE telle quelle. Aucune suppression n’a été enregistrée.
                </span>
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
